/**
 * Functional Area Dashboard Configurations
 *
 * Each config defines the KPIs, quick actions, and sub-page links
 * for a functional area dashboard. Cross-references the Corporate OS
 * template nav items in corporate_os_template.py.
 *
 * WQ-087a: 6 priority areas (executive, finance, people-culture,
 *          sales-marketing, operations, engineering-rd)
 * WQ-087b: 13 additional areas — 10 from guidance + 3 for unmapped
 *          template groups (product, bizdev, partnerships)
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface KPIConfig {
  id: string;
  label: string;
  icon: string;              // Lucide icon name (mapped via IconResolver)
  value: string;             // Display value (stubbed)
  change?: string;           // e.g., "+12%" or "-3%"
  changeDirection?: 'up' | 'down' | 'neutral';
  format: 'number' | 'currency' | 'percentage' | 'duration';
}

export interface QuickAction {
  label: string;
  icon: string;
  viewKey: string;           // Maps to view registry / nav item
}

export interface SubPageLink {
  label: string;
  description: string;
  viewKey: string;
  icon: string;
}

export interface FunctionalAreaConfig {
  areaKey: string;           // e.g., "finance", "people-culture"
  areaName: string;          // e.g., "Finance", "People & Culture"
  areaIcon: string;          // Lucide icon name
  description: string;       // One-line area description
  kpis: KPIConfig[];         // 3-4 KPI cards
  quickActions: QuickAction[];
  subPages: SubPageLink[];   // Links to the area's nav items
}

// ---------------------------------------------------------------------------
// Area Configs
// ---------------------------------------------------------------------------

const AREA_CONFIGS: Record<string, FunctionalAreaConfig> = {
  // 1. Executive
  executive: {
    areaKey: 'executive',
    areaName: 'Executive Overview',
    areaIcon: 'crown',
    description: 'Strategic leadership dashboard with cross-functional visibility into company performance, growth initiatives, and organizational health.',
    kpis: [
      { id: 'exec-revenue', label: 'Revenue', icon: 'dollar-sign', value: '$2.4M', change: '+8.3%', changeDirection: 'up', format: 'currency' },
      { id: 'exec-headcount', label: 'Headcount', icon: 'users', value: '142', change: '+6', changeDirection: 'up', format: 'number' },
      { id: 'exec-projects', label: 'Active Projects', icon: 'folder', value: '23', change: '-2', changeDirection: 'down', format: 'number' },
      { id: 'exec-compliance', label: 'Compliance Score', icon: 'shield', value: '94%', change: '+1.2%', changeDirection: 'up', format: 'percentage' },
    ],
    quickActions: [
      { label: 'Strategic Planning', icon: 'network', viewKey: 'exec-strategic-planning' },
      { label: 'Corporate KPIs', icon: 'gauge', viewKey: 'exec-kpis' },
      { label: 'Integration Map', icon: 'git-merge', viewKey: 'exec-integration' },
    ],
    subPages: [
      { label: 'Executive Overview', description: 'High-level performance dashboard with key metrics and trends', viewKey: 'exec-overview', icon: 'layout-dashboard' },
      { label: 'Strategic Planning', description: 'Growth vectors, market adjacencies, and strategic initiatives', viewKey: 'exec-strategic-planning', icon: 'network' },
      { label: 'Functional Integration Map', description: 'Cross-departmental data flows, dependencies, and health signals', viewKey: 'exec-integration', icon: 'git-merge' },
      { label: 'Corporate KPIs', description: 'Aggregated KPIs across all functional areas with trend analysis', viewKey: 'exec-kpis', icon: 'gauge' },
    ],
  },

  // 2. Finance
  finance: {
    areaKey: 'finance',
    areaName: 'Finance',
    areaIcon: 'dollar-sign',
    description: 'Financial operations dashboard with real-time visibility into revenue, expenses, budgets, and forecasting.',
    kpis: [
      { id: 'fin-revenue', label: 'Monthly Revenue', icon: 'trending-up', value: '$847K', change: '+5.2%', changeDirection: 'up', format: 'currency' },
      { id: 'fin-expenses', label: 'Expenses', icon: 'credit-card', value: '$612K', change: '+2.1%', changeDirection: 'up', format: 'currency' },
      { id: 'fin-margin', label: 'Gross Margin', icon: 'bar-chart', value: '27.8%', change: '+0.9%', changeDirection: 'up', format: 'percentage' },
      { id: 'fin-variance', label: 'Budget Variance', icon: 'activity', value: '-2.1%', change: '-0.3%', changeDirection: 'down', format: 'percentage' },
    ],
    quickActions: [
      { label: 'Financial Reports', icon: 'file-text', viewKey: 'finance-reports' },
      { label: 'Budget Planning', icon: 'activity', viewKey: 'finance-budgets' },
      { label: 'Accounts Payable', icon: 'credit-card', viewKey: 'finance-ap' },
    ],
    subPages: [
      { label: 'Overview', description: 'Financial health dashboard with cash flow, margins, and forecasts', viewKey: 'finance-overview', icon: 'bar-chart' },
      { label: 'Accounts Payable', description: 'Invoice management, payment scheduling, and AP aging analysis', viewKey: 'finance-ap', icon: 'credit-card' },
      { label: 'Accounts Receivable', description: 'Customer invoicing, payment tracking, and collections', viewKey: 'finance-ar', icon: 'trending-up' },
      { label: 'Budgets & Forecasts', description: 'Annual budgets, departmental allocations, and variance analysis', viewKey: 'finance-budgets', icon: 'activity' },
      { label: 'Financial Reports', description: 'P&L, balance sheet, cash flow, and custom reporting', viewKey: 'finance-reports', icon: 'file-text' },
    ],
  },

  // 3. People & Culture
  'people-culture': {
    areaKey: 'people-culture',
    areaName: 'People & Culture',
    areaIcon: 'users',
    description: 'Human resources dashboard covering talent acquisition, employee engagement, performance management, and organizational development.',
    kpis: [
      { id: 'hr-headcount', label: 'Total Headcount', icon: 'users', value: '142', change: '+6', changeDirection: 'up', format: 'number' },
      { id: 'hr-openings', label: 'Open Positions', icon: 'search', value: '8', change: '-3', changeDirection: 'down', format: 'number' },
      { id: 'hr-retention', label: 'Retention Rate', icon: 'user-check', value: '94.2%', change: '+0.8%', changeDirection: 'up', format: 'percentage' },
      { id: 'hr-training', label: 'Training Hours', icon: 'graduation-cap', value: '1,240', change: '+15%', changeDirection: 'up', format: 'number' },
    ],
    quickActions: [
      { label: 'People Directory', icon: 'users', viewKey: 'hr-directory' },
      { label: 'Recruiting', icon: 'search', viewKey: 'hr-recruiting' },
      { label: 'Performance', icon: 'award', viewKey: 'hr-performance' },
    ],
    subPages: [
      { label: 'People Directory', description: 'Employee profiles, org chart, and team structures', viewKey: 'hr-directory', icon: 'users' },
      { label: 'Recruiting Pipeline', description: 'Open positions, candidate tracking, and hiring funnel', viewKey: 'hr-recruiting', icon: 'search' },
      { label: 'Performance Reviews', description: 'Review cycles, goal tracking, and 360-degree feedback', viewKey: 'hr-performance', icon: 'award' },
      { label: 'Learning & Development', description: 'Training catalog, skill tracking, and certifications', viewKey: 'hr-learning', icon: 'book' },
    ],
  },

  // 4. Sales & Marketing
  'sales-marketing': {
    areaKey: 'sales-marketing',
    areaName: 'Sales & Marketing',
    areaIcon: 'briefcase',
    description: 'Unified sales and marketing dashboard with pipeline visibility, campaign performance, and customer acquisition metrics.',
    kpis: [
      { id: 'sm-pipeline', label: 'Pipeline Value', icon: 'trending-up', value: '$3.2M', change: '+12%', changeDirection: 'up', format: 'currency' },
      { id: 'sm-winrate', label: 'Win Rate', icon: 'target', value: '34%', change: '+2.1%', changeDirection: 'up', format: 'percentage' },
      { id: 'sm-mqls', label: 'MQLs This Month', icon: 'search', value: '187', change: '+23', changeDirection: 'up', format: 'number' },
      { id: 'sm-customers', label: 'Active Customers', icon: 'building', value: '89', change: '+5', changeDirection: 'up', format: 'number' },
    ],
    quickActions: [
      { label: 'Pipeline', icon: 'trending-up', viewKey: 'sales-pipeline' },
      { label: 'Campaigns', icon: 'zap', viewKey: 'marketing-campaigns' },
      { label: 'Deals', icon: 'dollar-sign', viewKey: 'sales-deals' },
      { label: 'Analytics', icon: 'bar-chart', viewKey: 'marketing-analytics' },
    ],
    subPages: [
      // Sales items
      { label: 'Pipeline', description: 'Opportunity tracking, stage progression, and deal velocity', viewKey: 'sales-pipeline', icon: 'trending-up' },
      { label: 'Deals', description: 'Active deal management, proposals, and close probability', viewKey: 'sales-deals', icon: 'dollar-sign' },
      { label: 'Sales Forecasts', description: 'Revenue forecasting and quota attainment tracking', viewKey: 'sales-forecasts', icon: 'bar-chart' },
      { label: 'Customer Accounts', description: 'Account profiles, engagement history, and expansion opportunities', viewKey: 'sales-accounts', icon: 'building' },
      // Marketing items
      { label: 'Campaigns', description: 'Campaign planning, execution, and ROI measurement', viewKey: 'marketing-campaigns', icon: 'zap' },
      { label: 'Content Calendar', description: 'Editorial planning and cross-team publishing coordination', viewKey: 'marketing-content', icon: 'calendar' },
      { label: 'Brand Assets', description: 'Digital asset management and brand guidelines', viewKey: 'marketing-brand', icon: 'image' },
      { label: 'Marketing Analytics', description: 'Attribution modeling, funnel analysis, and spend optimization', viewKey: 'marketing-analytics', icon: 'bar-chart' },
    ],
  },

  // 5. Operations
  operations: {
    areaKey: 'operations',
    areaName: 'Operations',
    areaIcon: 'clipboard',
    description: 'Operational excellence dashboard tracking system uptime, throughput, efficiency metrics, and vendor management.',
    kpis: [
      { id: 'ops-uptime', label: 'System Uptime', icon: 'server', value: '99.7%', change: '+0.1%', changeDirection: 'up', format: 'percentage' },
      { id: 'ops-throughput', label: 'Throughput', icon: 'activity', value: '1,247/day', change: '+8%', changeDirection: 'up', format: 'number' },
      { id: 'ops-efficiency', label: 'Efficiency Score', icon: 'gauge', value: '87%', change: '+3.2%', changeDirection: 'up', format: 'percentage' },
      { id: 'ops-issues', label: 'Open Issues', icon: 'alert-circle', value: '14', change: '-4', changeDirection: 'down', format: 'number' },
    ],
    quickActions: [
      { label: 'KPIs & Metrics', icon: 'bar-chart', viewKey: 'ops-kpis' },
      { label: 'Vendors', icon: 'building', viewKey: 'ops-vendors' },
      { label: 'Compliance', icon: 'shield', viewKey: 'ops-compliance' },
    ],
    subPages: [
      { label: 'KPIs & Metrics', description: 'Operational dashboards, trend analysis, and scorecards', viewKey: 'ops-kpis', icon: 'bar-chart' },
      { label: 'Vendor Management', description: 'Vendor profiles, contract tracking, and SLA monitoring', viewKey: 'ops-vendors', icon: 'building' },
      { label: 'Compliance', description: 'Regulatory tracking, audit prep, and policy management', viewKey: 'ops-compliance', icon: 'shield' },
      { label: 'Process Documentation', description: 'SOP library, process maps, and continuous improvement', viewKey: 'ops-processes', icon: 'file-text' },
    ],
  },

  // 6. Engineering & R&D
  'engineering-rd': {
    areaKey: 'engineering-rd',
    areaName: 'Engineering & R&D',
    areaIcon: 'wrench',
    description: 'Engineering and research dashboard covering sprint velocity, code quality, deployments, technical debt, and research initiatives.',
    kpis: [
      { id: 'eng-velocity', label: 'Sprint Velocity', icon: 'rocket', value: '42 pts', change: '+5', changeDirection: 'up', format: 'number' },
      { id: 'eng-defects', label: 'Defect Rate', icon: 'alert-triangle', value: '2.3%', change: '-0.4%', changeDirection: 'down', format: 'percentage' },
      { id: 'eng-releases', label: 'Release Cadence', icon: 'package', value: 'Bi-weekly', changeDirection: 'neutral', format: 'duration' },
      { id: 'eng-techdebt', label: 'Tech Debt Score', icon: 'activity', value: 'B+', change: '+1 grade', changeDirection: 'up', format: 'number' },
    ],
    quickActions: [
      { label: 'Projects', icon: 'folder', viewKey: 'engineering-projects' },
      { label: 'Deployments', icon: 'upload-cloud', viewKey: 'engineering-deploys' },
      { label: 'Research', icon: 'flask', viewKey: 'rnd-projects' },
    ],
    subPages: [
      // Engineering items
      { label: 'Projects', description: 'Project portfolio, resource allocation, and milestone tracking', viewKey: 'engineering-projects', icon: 'folder' },
      { label: 'Code Quality', description: 'Code review metrics, static analysis, and test coverage', viewKey: 'engineering-quality', icon: 'shield' },
      { label: 'Deployments', description: 'Pipeline status, release history, and environment health', viewKey: 'engineering-deploys', icon: 'upload-cloud' },
      { label: 'Technical Debt', description: 'Debt inventory, impact scoring, and remediation planning', viewKey: 'engineering-debt', icon: 'activity' },
      // R&D items
      { label: 'Research Projects', description: 'Active research initiatives and resource allocation', viewKey: 'rnd-projects', icon: 'folder' },
      { label: 'Experiments', description: 'Experiment design, execution, and results documentation', viewKey: 'rnd-experiments', icon: 'activity' },
      { label: 'Publications & Patents', description: 'Publication pipeline and patent application tracking', viewKey: 'rnd-publications', icon: 'file-text' },
      { label: 'R&D Budget', description: 'Investment tracking, project costs, and ROI analysis', viewKey: 'rnd-budgets', icon: 'dollar-sign' },
    ],
  },

  // -----------------------------------------------------------------------
  // WQ-087b: Remaining areas
  // -----------------------------------------------------------------------

  // 7. Legal & Compliance (template group: legal)
  'legal-compliance': {
    areaKey: 'legal-compliance',
    areaName: 'Legal & Compliance',
    areaIcon: 'scale',
    description: 'Legal operations dashboard covering contract lifecycle, regulatory compliance, intellectual property, and dispute management.',
    kpis: [
      { id: 'legal-contracts', label: 'Active Contracts', icon: 'file-text', value: '234', change: '+12', changeDirection: 'up', format: 'number' },
      { id: 'legal-score', label: 'Compliance Score', icon: 'shield', value: '96%', change: '+1.4%', changeDirection: 'up', format: 'percentage' },
      { id: 'legal-reviews', label: 'Pending Reviews', icon: 'clock', value: '12', change: '-3', changeDirection: 'down', format: 'number' },
      { id: 'legal-risk', label: 'Risk Items', icon: 'alert-triangle', value: '3', change: '-1', changeDirection: 'down', format: 'number' },
    ],
    quickActions: [
      { label: 'Contracts', icon: 'file-text', viewKey: 'legal-contracts' },
      { label: 'Compliance', icon: 'shield', viewKey: 'legal-compliance' },
      { label: 'IP Portfolio', icon: 'lock', viewKey: 'legal-ip' },
    ],
    subPages: [
      { label: 'Contracts', description: 'Contract lifecycle management, renewals, and obligation tracking', viewKey: 'legal-contracts', icon: 'file-text' },
      { label: 'Regulatory Compliance', description: 'Compliance framework tracking, audit readiness, and policy management', viewKey: 'legal-compliance', icon: 'shield' },
      { label: 'Intellectual Property', description: 'Patent portfolio, trademark registry, and trade secret management', viewKey: 'legal-ip', icon: 'lock' },
      { label: 'Disputes & Litigation', description: 'Active disputes, litigation timeline, and resolution tracking', viewKey: 'legal-disputes', icon: 'alert-triangle' },
    ],
  },

  // 8. IT & Infrastructure (template group: it)
  'it-infrastructure': {
    areaKey: 'it-infrastructure',
    areaName: 'IT & Infrastructure',
    areaIcon: 'monitor',
    description: 'IT operations dashboard tracking system uptime, help desk performance, asset management, and security posture.',
    kpis: [
      { id: 'it-uptime', label: 'System Uptime', icon: 'server', value: '99.9%', change: '+0.1%', changeDirection: 'up', format: 'percentage' },
      { id: 'it-tickets', label: 'Tickets Open', icon: 'help-circle', value: '47', change: '-8', changeDirection: 'down', format: 'number' },
      { id: 'it-response', label: 'Mean Response', icon: 'clock', value: '2.4h', change: '-0.3h', changeDirection: 'down', format: 'duration' },
      { id: 'it-security', label: 'Security Score', icon: 'shield', value: 'A', change: '+1', changeDirection: 'up', format: 'number' },
    ],
    quickActions: [
      { label: 'Infrastructure', icon: 'server', viewKey: 'it-infra' },
      { label: 'Help Desk', icon: 'help-circle', viewKey: 'it-helpdesk' },
      { label: 'Security', icon: 'lock', viewKey: 'it-security' },
    ],
    subPages: [
      { label: 'Infrastructure', description: 'Server monitoring, network health, and capacity planning', viewKey: 'it-infra', icon: 'server' },
      { label: 'Help Desk', description: 'Ticket management, SLA tracking, and user support metrics', viewKey: 'it-helpdesk', icon: 'help-circle' },
      { label: 'Asset Management', description: 'Hardware and software inventory, lifecycle tracking, and license compliance', viewKey: 'it-assets', icon: 'box' },
      { label: 'Security & Compliance', description: 'Security posture, vulnerability management, and compliance dashboards', viewKey: 'it-security', icon: 'lock' },
    ],
  },

  // 9. Customer Success
  'customer-success': {
    areaKey: 'customer-success',
    areaName: 'Customer Success',
    areaIcon: 'heart',
    description: 'Customer success dashboard tracking satisfaction, retention, account health, and support performance.',
    kpis: [
      { id: 'cs-nps', label: 'NPS Score', icon: 'trending-up', value: '72', change: '+4', changeDirection: 'up', format: 'number' },
      { id: 'cs-accounts', label: 'Active Accounts', icon: 'building', value: '89', change: '+7', changeDirection: 'up', format: 'number' },
      { id: 'cs-churn', label: 'Churn Rate', icon: 'trending-down', value: '1.2%', change: '-0.3%', changeDirection: 'down', format: 'percentage' },
      { id: 'cs-response', label: 'Avg Response Time', icon: 'clock', value: '4h', change: '-1h', changeDirection: 'down', format: 'duration' },
    ],
    quickActions: [
      { label: 'Account Health', icon: 'heart', viewKey: 'cs-health' },
      { label: 'Support Tickets', icon: 'help-circle', viewKey: 'cs-tickets' },
      { label: 'Onboarding', icon: 'user-plus', viewKey: 'cs-onboarding' },
    ],
    subPages: [
      { label: 'Account Health', description: 'Customer health scores, risk indicators, and engagement metrics', viewKey: 'cs-health', icon: 'heart' },
      { label: 'Support Tickets', description: 'Ticket volume, resolution times, and satisfaction scores', viewKey: 'cs-tickets', icon: 'help-circle' },
      { label: 'Onboarding', description: 'New customer onboarding status, milestones, and time-to-value', viewKey: 'cs-onboarding', icon: 'user-plus' },
      { label: 'Customer Feedback', description: 'NPS surveys, feature requests, and voice-of-customer analysis', viewKey: 'cs-feedback', icon: 'message-square' },
    ],
  },

  // 10. Project Management (template group: program-mgmt)
  'project-management': {
    areaKey: 'project-management',
    areaName: 'Program Management',
    areaIcon: 'gantt-chart',
    description: 'Program management dashboard tracking certifications, milestones, project status, and compliance initiatives.',
    kpis: [
      { id: 'pm-projects', label: 'Active Projects', icon: 'folder', value: '23', change: '+2', changeDirection: 'up', format: 'number' },
      { id: 'pm-ontrack', label: 'On-Track', icon: 'check-circle', value: '78%', change: '+3%', changeDirection: 'up', format: 'percentage' },
      { id: 'pm-atrisk', label: 'At-Risk', icon: 'alert-triangle', value: '4', change: '-1', changeDirection: 'down', format: 'number' },
      { id: 'pm-completed', label: 'Completed This Month', icon: 'award', value: '7', change: '+2', changeDirection: 'up', format: 'number' },
    ],
    quickActions: [
      { label: 'Overview', icon: 'layout-dashboard', viewKey: 'pm-overview' },
      { label: 'Milestones', icon: 'calendar', viewKey: 'pm-milestones' },
      { label: 'CMMC', icon: 'shield-check', viewKey: 'pm-cert-cmmc' },
    ],
    subPages: [
      { label: 'Overview', description: 'Program portfolio status, resource allocation, and milestone tracking', viewKey: 'pm-overview', icon: 'layout-dashboard' },
      { label: 'CMMC Certification', description: 'Cybersecurity Maturity Model Certification tracking across 3 levels', viewKey: 'pm-cert-cmmc', icon: 'shield-check' },
      { label: 'ISO 9001', description: 'Quality management system certification and audit tracking', viewKey: 'pm-cert-iso9001', icon: 'award' },
      { label: 'IECEx QMS', description: 'Explosive atmospheres quality management system compliance', viewKey: 'pm-cert-iecex', icon: 'zap' },
      { label: 'Milestones & Timeline', description: 'Cross-program milestone tracking and dependency management', viewKey: 'pm-milestones', icon: 'calendar' },
    ],
  },

  // 11. Supply Chain & Procurement
  'supply-chain': {
    areaKey: 'supply-chain',
    areaName: 'Supply Chain & Procurement',
    areaIcon: 'truck',
    description: 'Supply chain dashboard tracking purchase orders, delivery performance, inventory management, and supplier relationships.',
    kpis: [
      { id: 'sc-pos', label: 'Active POs', icon: 'file-text', value: '156', change: '+14', changeDirection: 'up', format: 'number' },
      { id: 'sc-ontime', label: 'On-Time Delivery', icon: 'check-circle', value: '94%', change: '+1.2%', changeDirection: 'up', format: 'percentage' },
      { id: 'sc-inventory', label: 'Inventory Value', icon: 'box', value: '$1.2M', change: '-$80K', changeDirection: 'down', format: 'currency' },
      { id: 'sc-leadtime', label: 'Avg Lead Time', icon: 'clock', value: '12d', change: '-2d', changeDirection: 'down', format: 'duration' },
    ],
    quickActions: [
      { label: 'Purchase Orders', icon: 'file-text', viewKey: 'sc-orders' },
      { label: 'Suppliers', icon: 'building', viewKey: 'sc-suppliers' },
      { label: 'Inventory', icon: 'box', viewKey: 'sc-inventory' },
    ],
    subPages: [
      { label: 'Purchase Orders', description: 'PO management, approval workflows, and spend tracking', viewKey: 'sc-orders', icon: 'file-text' },
      { label: 'Supplier Management', description: 'Supplier profiles, performance scorecards, and risk assessment', viewKey: 'sc-suppliers', icon: 'building' },
      { label: 'Inventory', description: 'Stock levels, reorder points, and warehouse management', viewKey: 'sc-inventory', icon: 'box' },
      { label: 'Logistics', description: 'Shipment tracking, carrier performance, and delivery optimization', viewKey: 'sc-logistics', icon: 'truck' },
    ],
  },

  // 12. Quality Assurance (template group: quality)
  'quality-assurance': {
    areaKey: 'quality-assurance',
    areaName: 'Quality Assurance',
    areaIcon: 'check-circle',
    description: 'Quality management dashboard tracking defect rates, audit results, corrective actions, and customer complaints.',
    kpis: [
      { id: 'qa-defect', label: 'Defect Rate', icon: 'alert-circle', value: '0.8%', change: '-0.2%', changeDirection: 'down', format: 'percentage' },
      { id: 'qa-audits', label: 'Audits Completed', icon: 'clipboard', value: '14', change: '+3', changeDirection: 'up', format: 'number' },
      { id: 'qa-capas', label: 'CAPAs Open', icon: 'refresh-cw', value: '6', change: '-2', changeDirection: 'down', format: 'number' },
      { id: 'qa-complaints', label: 'Customer Complaints', icon: 'message-square', value: '3', change: '-1', changeDirection: 'down', format: 'number' },
    ],
    quickActions: [
      { label: 'Overview', icon: 'bar-chart', viewKey: 'quality-overview' },
      { label: 'Audits', icon: 'clipboard', viewKey: 'quality-audits' },
      { label: 'CAPA', icon: 'refresh-cw', viewKey: 'quality-capa' },
    ],
    subPages: [
      { label: 'Overview', description: 'Quality metrics dashboard, trend analysis, and compliance scorecards', viewKey: 'quality-overview', icon: 'bar-chart' },
      { label: 'Internal Audits', description: 'Audit scheduling, findings tracking, and corrective action monitoring', viewKey: 'quality-audits', icon: 'clipboard' },
      { label: 'Non-Conformance Reports', description: 'NCR tracking, root cause analysis, and disposition management', viewKey: 'quality-ncr', icon: 'alert-circle' },
      { label: 'CAPA Tracking', description: 'Corrective and preventive action management with effectiveness verification', viewKey: 'quality-capa', icon: 'refresh-cw' },
    ],
  },

  // 13. Data & Analytics
  'data-analytics': {
    areaKey: 'data-analytics',
    areaName: 'Data & Analytics',
    areaIcon: 'bar-chart-2',
    description: 'Data operations dashboard tracking report generation, dashboard usage, data quality, and query performance.',
    kpis: [
      { id: 'da-reports', label: 'Reports Generated', icon: 'file-text', value: '347', change: '+42', changeDirection: 'up', format: 'number' },
      { id: 'da-dashboards', label: 'Active Dashboards', icon: 'layout-dashboard', value: '28', change: '+3', changeDirection: 'up', format: 'number' },
      { id: 'da-quality', label: 'Data Quality Score', icon: 'shield', value: '91%', change: '+2.1%', changeDirection: 'up', format: 'percentage' },
      { id: 'da-queries', label: 'Query Volume', icon: 'database', value: '12K/day', change: '+8%', changeDirection: 'up', format: 'number' },
    ],
    quickActions: [
      { label: 'Dashboards', icon: 'layout-dashboard', viewKey: 'da-dashboards' },
      { label: 'Reports', icon: 'file-text', viewKey: 'da-reports' },
      { label: 'Data Quality', icon: 'shield', viewKey: 'da-quality' },
    ],
    subPages: [
      { label: 'Dashboards', description: 'Active dashboards, usage analytics, and performance monitoring', viewKey: 'da-dashboards', icon: 'layout-dashboard' },
      { label: 'Reports', description: 'Report catalog, scheduling, and distribution management', viewKey: 'da-reports', icon: 'file-text' },
      { label: 'Data Quality', description: 'Data profiling, validation rules, and quality scorecards', viewKey: 'da-quality', icon: 'shield' },
      { label: 'Data Catalog', description: 'Metadata management, data lineage, and glossary', viewKey: 'da-catalog', icon: 'database' },
    ],
  },

  // 14. Communications & PR
  'communications': {
    areaKey: 'communications',
    areaName: 'Communications & PR',
    areaIcon: 'megaphone',
    description: 'Corporate communications dashboard tracking press coverage, social media reach, internal comms, and brand sentiment.',
    kpis: [
      { id: 'comm-press', label: 'Press Mentions', icon: 'newspaper', value: '23', change: '+5', changeDirection: 'up', format: 'number' },
      { id: 'comm-reach', label: 'Social Reach', icon: 'globe', value: '145K', change: '+12%', changeDirection: 'up', format: 'number' },
      { id: 'comm-internal', label: 'Internal Comms Sent', icon: 'mail', value: '89', change: '+14', changeDirection: 'up', format: 'number' },
      { id: 'comm-sentiment', label: 'Sentiment Score', icon: 'heart', value: '82%', change: '+3%', changeDirection: 'up', format: 'percentage' },
    ],
    quickActions: [
      { label: 'Press Room', icon: 'newspaper', viewKey: 'comm-press' },
      { label: 'Social Media', icon: 'globe', viewKey: 'comm-social' },
      { label: 'Internal Comms', icon: 'mail', viewKey: 'comm-internal' },
    ],
    subPages: [
      { label: 'Press Room', description: 'Press releases, media coverage, and journalist contacts', viewKey: 'comm-press', icon: 'newspaper' },
      { label: 'Social Media', description: 'Social channel management, content scheduling, and engagement analytics', viewKey: 'comm-social', icon: 'globe' },
      { label: 'Internal Communications', description: 'Company announcements, newsletters, and town hall management', viewKey: 'comm-internal', icon: 'mail' },
      { label: 'Brand Monitoring', description: 'Sentiment analysis, competitive monitoring, and crisis detection', viewKey: 'comm-monitoring', icon: 'search' },
    ],
  },

  // 15. Facilities & Administration
  'facilities': {
    areaKey: 'facilities',
    areaName: 'Facilities & Administration',
    areaIcon: 'building-2',
    description: 'Facilities management dashboard tracking space utilization, maintenance, energy efficiency, and workplace services.',
    kpis: [
      { id: 'fac-locations', label: 'Locations', icon: 'map-pin', value: '4', changeDirection: 'neutral', format: 'number' },
      { id: 'fac-util', label: 'Utilization Rate', icon: 'bar-chart', value: '73%', change: '+5%', changeDirection: 'up', format: 'percentage' },
      { id: 'fac-tickets', label: 'Maintenance Tickets', icon: 'wrench', value: '18', change: '-4', changeDirection: 'down', format: 'number' },
      { id: 'fac-energy', label: 'Energy Efficiency', icon: 'zap', value: 'B+', change: '+1', changeDirection: 'up', format: 'number' },
    ],
    quickActions: [
      { label: 'Space Planning', icon: 'layout', viewKey: 'fac-spaces' },
      { label: 'Maintenance', icon: 'wrench', viewKey: 'fac-maintenance' },
      { label: 'Visitors', icon: 'user-plus', viewKey: 'fac-visitors' },
    ],
    subPages: [
      { label: 'Space Planning', description: 'Floor plans, desk booking, and space utilization analytics', viewKey: 'fac-spaces', icon: 'layout' },
      { label: 'Maintenance', description: 'Work orders, preventive maintenance schedules, and vendor coordination', viewKey: 'fac-maintenance', icon: 'wrench' },
      { label: 'Visitor Management', description: 'Visitor registration, badge printing, and access logging', viewKey: 'fac-visitors', icon: 'user-plus' },
      { label: 'Energy & Sustainability', description: 'Energy consumption monitoring, carbon footprint, and ESG reporting', viewKey: 'fac-energy', icon: 'zap' },
    ],
  },

  // 16. Innovation & Strategy
  'innovation': {
    areaKey: 'innovation',
    areaName: 'Innovation & Strategy',
    areaIcon: 'lightbulb',
    description: 'Innovation pipeline dashboard tracking ideas, proof-of-concept initiatives, patent filings, and R&D investment.',
    kpis: [
      { id: 'innov-pipeline', label: 'Ideas Pipeline', icon: 'lightbulb', value: '34', change: '+8', changeDirection: 'up', format: 'number' },
      { id: 'innov-pocs', label: 'Active POCs', icon: 'flask', value: '5', change: '+1', changeDirection: 'up', format: 'number' },
      { id: 'innov-patents', label: 'Patents Filed', icon: 'award', value: '2', change: '+1', changeDirection: 'up', format: 'number' },
      { id: 'innov-budget', label: 'Budget Remaining', icon: 'dollar-sign', value: '67%', change: '-8%', changeDirection: 'down', format: 'percentage' },
    ],
    quickActions: [
      { label: 'Ideas Board', icon: 'lightbulb', viewKey: 'innov-ideas' },
      { label: 'POC Tracker', icon: 'flask', viewKey: 'innov-pocs' },
      { label: 'Patents', icon: 'award', viewKey: 'innov-patents' },
    ],
    subPages: [
      { label: 'Ideas Board', description: 'Innovation idea submission, voting, and prioritization', viewKey: 'innov-ideas', icon: 'lightbulb' },
      { label: 'POC Tracker', description: 'Proof-of-concept project management and success criteria tracking', viewKey: 'innov-pocs', icon: 'flask' },
      { label: 'Patents & IP', description: 'Patent application pipeline and intellectual property portfolio', viewKey: 'innov-patents', icon: 'award' },
      { label: 'Innovation Budget', description: 'R&D investment tracking, allocation, and ROI analysis', viewKey: 'innov-budget', icon: 'dollar-sign' },
    ],
  },

  // -----------------------------------------------------------------------
  // WQ-087b: Additional template group areas (not in original 10 guidance)
  // -----------------------------------------------------------------------

  // Product Development (template group: product)
  'product': {
    areaKey: 'product',
    areaName: 'Product Development',
    areaIcon: 'lightbulb',
    description: 'Product development dashboard tracking roadmap progress, feature requests, sprint execution, and release management.',
    kpis: [
      { id: 'prod-features', label: 'Feature Requests', icon: 'layers', value: '47', change: '+12', changeDirection: 'up', format: 'number' },
      { id: 'prod-velocity', label: 'Sprint Velocity', icon: 'activity', value: '38 pts', change: '+4', changeDirection: 'up', format: 'number' },
      { id: 'prod-releases', label: 'Releases This Quarter', icon: 'package', value: '6', change: '+1', changeDirection: 'up', format: 'number' },
      { id: 'prod-satisfaction', label: 'User Satisfaction', icon: 'heart', value: '4.2/5', change: '+0.2', changeDirection: 'up', format: 'number' },
    ],
    quickActions: [
      { label: 'Roadmap', icon: 'map', viewKey: 'product-roadmap' },
      { label: 'Features', icon: 'layers', viewKey: 'product-features' },
      { label: 'Releases', icon: 'file-text', viewKey: 'product-releases' },
    ],
    subPages: [
      { label: 'Roadmap', description: 'Product roadmap visualization, timeline, and strategic alignment', viewKey: 'product-roadmap', icon: 'map' },
      { label: 'Feature Requests', description: 'Feature backlog, prioritization scoring, and customer demand signals', viewKey: 'product-features', icon: 'layers' },
      { label: 'Sprint Tracker', description: 'Sprint planning, burndown charts, and velocity tracking', viewKey: 'product-sprints', icon: 'activity' },
      { label: 'Release Notes', description: 'Release history, changelog management, and communication templates', viewKey: 'product-releases', icon: 'file-text' },
    ],
  },

  // New Business Development (template group: bizdev)
  'bizdev': {
    areaKey: 'bizdev',
    areaName: 'New Business Development',
    areaIcon: 'target',
    description: 'Business development dashboard tracking opportunities, proposals, win/loss analysis, and partner ecosystem.',
    kpis: [
      { id: 'bd-opps', label: 'Open Opportunities', icon: 'search', value: '34', change: '+6', changeDirection: 'up', format: 'number' },
      { id: 'bd-proposals', label: 'Active Proposals', icon: 'file-text', value: '12', change: '+3', changeDirection: 'up', format: 'number' },
      { id: 'bd-winrate', label: 'Win Rate', icon: 'trending-up', value: '42%', change: '+5%', changeDirection: 'up', format: 'percentage' },
      { id: 'bd-pipeline', label: 'Pipeline Value', icon: 'dollar-sign', value: '$4.8M', change: '+$1.2M', changeDirection: 'up', format: 'currency' },
    ],
    quickActions: [
      { label: 'Opportunities', icon: 'search', viewKey: 'bizdev-opportunities' },
      { label: 'Proposals', icon: 'file-text', viewKey: 'bizdev-proposals' },
      { label: 'Win/Loss', icon: 'bar-chart', viewKey: 'bizdev-winloss' },
    ],
    subPages: [
      { label: 'Opportunities', description: 'Opportunity pipeline, qualification criteria, and pursuit decisions', viewKey: 'bizdev-opportunities', icon: 'search' },
      { label: 'Proposals & RFPs', description: 'Proposal development, pricing strategies, and submission tracking', viewKey: 'bizdev-proposals', icon: 'file-text' },
      { label: 'Win/Loss Analysis', description: 'Competitive analysis, win themes, and lessons learned', viewKey: 'bizdev-winloss', icon: 'bar-chart' },
      { label: 'Partner Ecosystem', description: 'Strategic partner relationships and teaming arrangements', viewKey: 'bizdev-partners', icon: 'handshake' },
    ],
  },

  // Strategic Partnerships (template group: partnerships)
  'partnerships': {
    areaKey: 'partnerships',
    areaName: 'Strategic Partnerships',
    areaIcon: 'handshake',
    description: 'Partnership management dashboard tracking partner programs, performance metrics, and joint pipeline development.',
    kpis: [
      { id: 'part-active', label: 'Active Partners', icon: 'handshake', value: '18', change: '+3', changeDirection: 'up', format: 'number' },
      { id: 'part-revenue', label: 'Partner Revenue', icon: 'dollar-sign', value: '$890K', change: '+15%', changeDirection: 'up', format: 'currency' },
      { id: 'part-pipeline', label: 'Joint Pipeline', icon: 'git-merge', value: '$2.1M', change: '+$400K', changeDirection: 'up', format: 'currency' },
      { id: 'part-satisfaction', label: 'Partner NPS', icon: 'trending-up', value: '68', change: '+4', changeDirection: 'up', format: 'number' },
    ],
    quickActions: [
      { label: 'Partners', icon: 'layout-dashboard', viewKey: 'partner-overview' },
      { label: 'Programs', icon: 'layers', viewKey: 'partner-programs' },
      { label: 'Pipeline', icon: 'git-merge', viewKey: 'partner-pipeline' },
    ],
    subPages: [
      { label: 'Partner Overview', description: 'Partner directory, tier status, and relationship health scores', viewKey: 'partner-overview', icon: 'layout-dashboard' },
      { label: 'Partner Programs', description: 'Program structure, tiers, benefits, and enrollment tracking', viewKey: 'partner-programs', icon: 'layers' },
      { label: 'Partner Performance', description: 'Revenue attribution, lead generation, and co-marketing metrics', viewKey: 'partner-performance', icon: 'trending-up' },
      { label: 'Joint Pipeline', description: 'Shared opportunities, deal registration, and co-selling activities', viewKey: 'partner-pipeline', icon: 'git-merge' },
    ],
  },
};

// ---------------------------------------------------------------------------
// Group-to-Area Mapping
// ---------------------------------------------------------------------------

/**
 * Maps Corporate OS template group IDs to area dashboard config keys.
 * Groups not in this map don't have an area dashboard (show stub instead).
 *
 * Note: sales + marketing both map to 'sales-marketing' composite area.
 * engineering + rnd both map to 'engineering-rd' composite area.
 *
 * WQ-087b: Added mappings for remaining template groups (program-mgmt,
 * product, bizdev, it, legal, quality, partnerships).
 */
export const GROUP_TO_AREA_MAP: Record<string, string> = {
  // WQ-087a: Original 6 area mappings
  'executive': 'executive',
  'finance': 'finance',
  'hr': 'people-culture',
  'sales': 'sales-marketing',
  'marketing': 'sales-marketing',
  'operations': 'operations',
  'engineering': 'engineering-rd',
  'rnd': 'engineering-rd',
  // WQ-087b: Remaining template group mappings
  'program-mgmt': 'project-management',
  'product': 'product',
  'bizdev': 'bizdev',
  'it': 'it-infrastructure',
  'legal': 'legal-compliance',
  'quality': 'quality-assurance',
  'partnerships': 'partnerships',
};

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export function getAreaConfig(areaKey: string): FunctionalAreaConfig | undefined {
  return AREA_CONFIGS[areaKey];
}

export function getAreaConfigForGroup(groupId: string): FunctionalAreaConfig | undefined {
  const areaKey = GROUP_TO_AREA_MAP[groupId];
  return areaKey ? AREA_CONFIGS[areaKey] : undefined;
}

export function hasAreaDashboard(groupId: string): boolean {
  return groupId in GROUP_TO_AREA_MAP;
}

export default AREA_CONFIGS;
